import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Container } from '@/components/container';
import { useSnackbar } from 'notistack';
import Select, { components, MultiValue, NoticeProps } from 'react-select';
import { CampaignStatus } from '@/constant';
import {
  useCreateAdvertiseMutation,
  useGetAdvertiseByIdQuery,
  useGetSlotsQuery,
  useUpdateAdvertiseMutation,
} from '@/services/AdvertiseManagementService';
import { IAdvertiseData, AdSlot } from '@/types/advertise-management.types';
import MediaUpload from '../habor-management/MediaUpload';
import { formatFinnishPercentage } from '@/utils';

interface SelectOption {
  value: string;
  label: string;
}

interface AdvertiseFormProps {
  mode: 'create' | 'edit';
}

// react-select renders its "No options" notice inside role="listbox" without an
// option role; give it one so the listbox has valid children
const NoOptionsMessage = <Option, IsMulti extends boolean>(
  props: NoticeProps<Option, IsMulti>
) => (
  <components.NoOptionsMessage
    {...props}
    innerProps={{
      ...props.innerProps,
      role: 'option',
      'aria-disabled': true,
      'aria-selected': false,
    }}
  />
);

const AdvertiseForm = ({ mode }: AdvertiseFormProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [createAdvertise, { isLoading: isCreating }] =
    useCreateAdvertiseMutation();
  const [updateAdvertise, { isLoading: isUpdating }] =
    useUpdateAdvertiseMutation();

  const isEdit = mode === 'edit';
  const advertiseId = isEdit ? params.id : undefined;
  const isViewMode = searchParams.get('mode') === 'view';
  const isReadOnly = isViewMode;

  const [formData, setFormData] = useState<Partial<IAdvertiseData>>({
    ad_name: '',
    description: '',
    slot_ids: [],
    start_date: '',
    end_date: '',
    media: null,
    media_captions: [],
    target_url: '',
    status: CampaignStatus.DRAFT,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Slots
  const { data: slotsData, isLoading: slotsLoading } = useGetSlotsQuery();
  const slotOptions: SelectOption[] = useMemo(
    () =>
      slotsData?.data?.map((s) => ({
        value: s.id,
        label: `${s.name} - ${s.position}`,
      })) || [],
    [slotsData]
  );

  // Fetch details in edit mode
  const { data: advertiseDetail, isFetching: fetchingAd } =
    useGetAdvertiseByIdQuery(advertiseId!, {
      skip: !isEdit || !advertiseId,
    });

  useEffect(() => {
    if (advertiseDetail && isEdit) {
      const a = advertiseDetail;
      const toDateOnly = (v?: string) =>
        v && v.includes('T') ? v.split('T')[0] : v || '';

      // Handle media from API
      const media =
        a.image_url || (typeof a.image === 'string' ? a.image : null);

      setFormData({
        id: a.id,
        ad_name: a.ad_name || a.name || '',
        description: a.description || '',
        slot_ids: a.slot_ids || (a.slot_id ? [a.slot_id] : []),
        start_date: toDateOnly(a.start_date),
        end_date: toDateOnly(a.end_date),
        media: media,
        target_url: a.target_url,
        status: a.status,
        media_captions:
          a.image_captions && typeof a.image_captions === 'object'
            ? [
                {
                  image_index: a.image_captions.image_index || 0,
                  captions: a.image_captions.captions || {},
                },
              ]
            : [],
      });
      setErrors({});
    }
  }, [advertiseDetail, isEdit]);

  // Validation schemas
  const createSchema = useMemo(
    () =>
      Yup.object().shape({
        ad_name: Yup.string()
          .required('Advertisement name is required')
          .min(2, 'Advertisement name must be at least 2 characters')
          .max(100, 'Advertisement name must not exceed 100 characters'),
        description: Yup.string().max(
          500,
          'Description must not exceed 500 characters'
        ),
        slot_ids: Yup.array()
          .of(Yup.string())
          .min(1, 'At least one slot must be selected')
          .required('Slot selection is required'),
        start_date: Yup.date()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .typeError('Please select a valid start date')
          .required('Start date is required')
          .min(
            new Date(new Date().setHours(0, 0, 0, 0)),
            'Start date cannot be in the past'
          ),
        end_date: Yup.date()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .typeError('Please select a valid end date')
          .required('End date is required')
          .min(Yup.ref('start_date'), 'End date must be after start date'),
        target_url: Yup.string()
          .required('Target URL is required')
          .url('Please enter a valid URL'),
        media: Yup.mixed().required('Media is required'),
      }),
    []
  );

  const editSchema = useMemo(
    () =>
      Yup.object().shape({
        ad_name: Yup.string()
          .required('Advertisement name is required')
          .min(2, 'Advertisement name must be at least 2 characters')
          .max(100, 'Advertisement name must not exceed 100 characters'),
        slot_ids: Yup.array()
          .of(Yup.string())
          .min(1, 'At least one slot must be selected'),
        start_date: Yup.date()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .typeError('Please select a valid start date')
          .required('Start date is required'),
        end_date: Yup.date()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .typeError('Please select a valid end date')
          .required('End date is required')
          .min(Yup.ref('start_date'), 'End date must be after start date'),
        target_url: Yup.string()
          .required('Target URL is required')
          .url('Please enter a valid URL'),
        media: Yup.mixed().nullable(),
      }),
    []
  );

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleSlotsChange = useCallback(
    (selectedOptions: MultiValue<SelectOption>) => {
      const selected = selectedOptions
        ? Array.from(selectedOptions).map((o) => o.value)
        : [];
      setFormData((prev) => ({ ...prev, slot_ids: selected }));
    },
    []
  );

  const handleBlur = useCallback(
    (field: string) => {
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    },
    [errors]
  );

  const getDateOnly = useCallback((v?: string) => {
    return v && v.includes('T') ? v.split('T')[0] : v || '';
  }, []);

  const normalizeStartDateUTC = useCallback((dateStr: string) => {
    return `${dateStr}T00:00:00Z`;
  }, []);

  const normalizeEndDateUTC = useCallback((dateStr: string) => {
    return `${dateStr}T23:59:59Z`;
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      if (isEdit) {
        await editSchema.validate(formData, { abortEarly: false });
        if (!formData || !formData.id) return;

        // Build FormData with changed fields only
        const payload = new FormData();
        const original = advertiseDetail as IAdvertiseData | undefined;

        if (!original) return;

        if (formData.ad_name !== original.ad_name)
          payload.append('name', formData.ad_name || '');
        if (formData.description !== original.description)
          payload.append('description', formData.description || '');
        const originalStartDateOnly = getDateOnly(original.start_date);
        const originalEndDateOnly = getDateOnly(original.end_date);

        if (
          formData.start_date &&
          formData.start_date !== originalStartDateOnly
        )
          payload.append(
            'start_date',
            normalizeStartDateUTC(formData.start_date)
          );
        if (formData.end_date && formData.end_date !== originalEndDateOnly)
          payload.append('end_date', normalizeEndDateUTC(formData.end_date));
        if (formData.target_url !== original.target_url)
          payload.append('target_url', formData.target_url || '');
        if (
          typeof formData.status === 'string' &&
          formData.status !== original.status
        )
          payload.append('status', formData.status);

        if (
          formData.slot_ids &&
          JSON.stringify(formData.slot_ids) !==
            JSON.stringify(original.slot_ids)
        ) {
          formData.slot_ids.forEach((id) => payload.append('slot_ids[]', id));
        }

        if (formData.media) {
          if (formData.media instanceof File) {
            payload.append('image', formData.media);
          }
        }

        if (formData.media_captions && formData.media_captions.length > 0) {
          payload.append(
            'image_captions',
            JSON.stringify(formData.media_captions[0])
          );
        }

        await updateAdvertise({ id: formData.id, formData: payload }).unwrap();
        enqueueSnackbar('Advertisement updated successfully', {
          variant: 'success',
        });
        navigate('/advertise-management');
        return;
      }

      await createSchema.validate(formData, { abortEarly: false });
      const fd = new FormData();
      if (formData.ad_name) fd.append('name', formData.ad_name);
      if (typeof formData.description === 'string')
        fd.append('description', formData.description);
      if (formData.slot_ids && formData.slot_ids.length > 0)
        formData.slot_ids.forEach((id) => fd.append('slot_ids[]', id));
      if (formData.start_date)
        fd.append('start_date', normalizeStartDateUTC(formData.start_date));
      if (formData.end_date)
        fd.append('end_date', normalizeEndDateUTC(formData.end_date));
      if (formData.target_url) fd.append('target_url', formData.target_url);
      if (typeof formData.status === 'string')
        fd.append('status', formData.status);

      if (formData.media) {
        if (formData.media instanceof File) {
          fd.append('image', formData.media);
        }
      }

      if (formData.media_captions && formData.media_captions.length > 0) {
        fd.append('image_captions', JSON.stringify(formData.media_captions[0]));
      }

      await createAdvertise(fd).unwrap();
      enqueueSnackbar('Advertisement created successfully', {
        variant: 'success',
      });
      navigate('/advertise-management');
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        // Extract backend error message
        const backendError = extractBackendError(err);
        enqueueSnackbar(backendError, { variant: 'error' });
      }
    }
  }, [
    isEdit,
    formData,
    editSchema,
    createSchema,
    updateAdvertise,
    createAdvertise,
    enqueueSnackbar,
    navigate,
    advertiseDetail,
    getDateOnly,
    normalizeStartDateUTC,
    normalizeEndDateUTC,
  ]);

  // Helper function to extract backend error messages
  const extractBackendError = (err: unknown): string => {
    const error = err as Record<string, unknown>;

    // Check for RTK Query error structure
    if (error?.data) {
      const data = error.data as Record<string, unknown>;

      // Handle message field
      if (typeof data.message === 'string') {
        return data.message;
      }

      // Handle errors field (array)
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        const firstError = data.errors[0];
        if (typeof firstError === 'string') {
          return firstError;
        }
      }

      // Handle errors field (object)
      if (typeof data.errors === 'object' && data.errors !== null) {
        const errorMessages = Object.values(data.errors)
          .flat()
          .filter((msg): msg is string => typeof msg === 'string');
        if (errorMessages.length > 0) {
          return errorMessages.join(', ');
        }
      }
    }

    // Fallback to generic error message
    return 'An error occurred while processing your request';
  };

  const isSubmitting = isCreating || isUpdating || fetchingAd;

  return (
    <Container>
      <section role="region" aria-labelledby="advertise-form-title">
        <div className="flex items-center justify-between mb-6">
          <h1 id="advertise-form-title" className="text-xl font-semibold">
            {isViewMode
              ? 'View Advertisement'
              : isEdit
                ? 'Edit Advertisement'
                : 'Create Advertisement'}
          </h1>
          <div className="flex gap-2">
            <button
              className="btn btn-light"
              type="button"
              onClick={() => navigate('/advertise-management')}
              disabled={isSubmitting}
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                className="btn btn-primary"
                type="button"
                onClick={onSubmit}
                disabled={isSubmitting}
              >
                {isEdit ? 'Save Changes' : 'Create'}
              </button>
            )}
          </div>
        </div>

        {/* Performance Metrics - Only in View Mode */}
        {isViewMode && advertiseDetail && (
          <div className="card mb-6">
            <div className="card-body">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                Performance Metrics
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Impressions */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                        Impressions
                      </p>
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {advertiseDetail.impressions?.toLocaleString() || '0'}
                      </p>
                    </div>
                    <div className="bg-blue-200 rounded-full p-2">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Clicks */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-green-700 dark:text-green-600 contrast:text-green-600 uppercase tracking-wide">
                        Clicks
                      </p>
                      <p className="text-2xl font-bold text-green-900 mt-1">
                        {advertiseDetail.clicks?.toLocaleString() || '0'}
                      </p>
                    </div>
                    <div className="bg-green-200 rounded-full p-2">
                      <svg
                        className="w-5 h-5 text-green-700 dark:text-green-600 contrast:text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Click-Through Rate */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">
                        CTR
                      </p>
                      <p className="text-2xl font-bold text-purple-900 mt-1">
                        {advertiseDetail.impressions &&
                        advertiseDetail.impressions > 0
                          ? formatFinnishPercentage(
                              ((advertiseDetail.clicks || 0) /
                                advertiseDetail.impressions) *
                                100,
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )
                          : formatFinnishPercentage(0, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                      </p>
                    </div>
                    <div className="bg-purple-200 rounded-full p-2">
                      <svg
                        className="w-5 h-5 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Ad Slots */}
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-orange-600 uppercase tracking-wide">
                        Ad Slots
                      </p>
                      <p className="text-2xl font-bold text-orange-900 mt-1">
                        {advertiseDetail.ad_slots?.length || 0}
                      </p>
                    </div>
                    <div className="bg-orange-200 rounded-full p-2">
                      <svg
                        className="w-5 h-5 text-orange-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ad Slots Details */}
              {advertiseDetail.ad_slots &&
                advertiseDetail.ad_slots.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">
                      Advertisement Slot Details
                    </h3>
                    <div className="bg-gray-100 rounded-lg p-4">
                      <div className="space-y-3">
                        {advertiseDetail.ad_slots.map(
                          (slot: AdSlot, index: number) => (
                            <div
                              key={slot.id}
                              className="flex items-center justify-between bg-gray-200 rounded-lg p-3 border border-gray-200"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center">
                                  <span className="text-blue-600 contrast:text-blue-900 font-semibold text-sm">
                                    {index + 1}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {slot.name}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {slot.type} • {slot.position} •{' '}
                                    {slot.placement}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span
                                  className={clsx(
                                    'px-2 py-1 rounded-full text-xs font-medium',
                                    slot.status === 'active'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  )}
                                >
                                  {slot.status?.charAt(0).toUpperCase() +
                                    slot.status?.slice(1)}
                                </span>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label
                  htmlFor="ad_name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  aria-label="Advertisement Name"
                >
                  Advertisement Name<span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  id="ad_name"
                  name="ad_name"
                  value={formData.ad_name || ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('ad_name')}
                  disabled={isSubmitting || isReadOnly}
                  readOnly={isReadOnly}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.ad_name && 'border-red-500',
                    isReadOnly && 'bg-gray-50 cursor-not-allowed'
                  )}
                  aria-invalid={!!errors.ad_name}
                  aria-describedby={
                    errors.ad_name ? 'ad_name-error' : undefined
                  }
                  autoComplete="off"
                />
                {errors.ad_name && (
                  <p
                    id="ad_name-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.ad_name}
                  </p>
                )}
              </div>

              {/* Target URL */}
              <div>
                <label
                  htmlFor="target_url"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  aria-label="Target URL"
                >
                  Target URL<span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="url"
                  id="target_url"
                  name="target_url"
                  value={formData.target_url || ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('target_url')}
                  disabled={isSubmitting || isReadOnly}
                  readOnly={isReadOnly}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.target_url && 'border-red-500',
                    isReadOnly && 'bg-gray-50 cursor-not-allowed'
                  )}
                  autoComplete="off"
                  aria-invalid={!!errors.target_url}
                  aria-describedby={
                    errors.target_url ? 'target_url-error' : undefined
                  }
                />
                {errors.target_url && (
                  <p
                    id="target_url-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.target_url}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  aria-label="Description"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('description')}
                  disabled={isSubmitting || isReadOnly}
                  readOnly={isReadOnly}
                  className={clsx(
                    'textarea textarea-bordered w-full rounded-lg border-gray-300',
                    errors.description && 'border-red-500',
                    isReadOnly && 'bg-gray-50 cursor-not-allowed'
                  )}
                  rows={4}
                  maxLength={500}
                  placeholder="Enter advertisement description (max 500 characters)"
                  aria-invalid={!!errors.description}
                  aria-describedby={
                    errors.description ? 'description-error' : undefined
                  }
                />
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-gray-500">
                    {(formData.description || '').length}/500 characters
                  </span>
                </div>
                {errors.description && (
                  <p
                    id="description-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Media Upload */}
              <div className="md:col-span-2">
                <MediaUpload
                  field="media"
                  media={formData.media}
                  setFormData={setFormData}
                  errors={errors}
                  setErrors={setErrors}
                  disabled={isSubmitting || isReadOnly}
                  allowedTypes={[
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                    'image/gif',
                    'image/webp',
                  ]}
                  maxFiles={1}
                  previewMode={true}
                  media_captions={formData.media_captions}
                  required
                  closeButton={false}
                />
              </div>

              {/* Slots */}
              <div className="md:col-span-2">
                <label
                  htmlFor="slot_ids"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  aria-label="Advertisement Slots"
                >
                  Advertisement Slots
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <Select
                  isMulti
                  name="slot_ids"
                  inputId="slot_ids"
                  options={slotOptions}
                  value={slotOptions.filter((o) =>
                    formData.slot_ids?.includes(o.value)
                  )}
                  onChange={handleSlotsChange}
                  isDisabled={isSubmitting || slotsLoading || isReadOnly}
                  placeholder={
                    slotsLoading ? 'Loading slots...' : 'Select slots'
                  }
                  className={clsx(
                    'react-select-container',
                    errors.slot_ids && 'border-red-500'
                  )}
                  classNamePrefix="react-select"
                  components={{ NoOptionsMessage }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '44px',
                      borderColor: errors.slot_ids ? '#ef4444' : '#d1d5db',
                    }),
                  }}
                  aria-invalid={!!errors.slot_ids}
                  aria-describedby={
                    errors.slot_ids ? 'slot_ids-error' : undefined
                  }
                />
                {errors.slot_ids && (
                  <p
                    id="slot_ids-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.slot_ids}
                  </p>
                )}
              </div>

              {/* Start Date */}
              <div>
                <label
                  htmlFor="start_date"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  aria-label="Start Date"
                >
                  Start Date<span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date || ''}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('start_date')}
                  disabled={isSubmitting || isReadOnly}
                  readOnly={isReadOnly}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.start_date && 'border-red-500',
                    isReadOnly && 'bg-gray-50 cursor-not-allowed'
                  )}
                  aria-invalid={!!errors.start_date}
                  aria-describedby={
                    errors.start_date ? 'start_date-error' : undefined
                  }
                  autoComplete="off"
                />
                {errors.start_date && (
                  <p
                    id="start_date-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.start_date}
                  </p>
                )}
              </div>

              {/* End Date */}
              <div>
                <label
                  htmlFor="end_date"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  aria-label="End Date"
                >
                  End Date<span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date || ''}
                  min={formData.start_date || undefined}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('end_date')}
                  disabled={isSubmitting || isReadOnly}
                  readOnly={isReadOnly}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.end_date && 'border-red-500',
                    isReadOnly && 'bg-gray-50 cursor-not-allowed'
                  )}
                  aria-invalid={!!errors.end_date}
                  aria-describedby={
                    errors.end_date ? 'end_date-error' : undefined
                  }
                  autoComplete="off"
                />
                {errors.end_date && (
                  <p
                    id="end_date-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.end_date}
                  </p>
                )}
              </div>

              {/* Status */}
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700 mb-2"
                  aria-label="Status"
                >
                  Status<span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  id="status"
                  name="status"
                  value={
                    typeof formData.status === 'string' ? formData.status : ''
                  }
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('status')}
                  disabled={isSubmitting || isReadOnly}
                  className={clsx(
                    'select select-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.status && 'border-red-500',
                    isReadOnly && 'bg-gray-50 cursor-not-allowed'
                  )}
                  aria-invalid={!!errors.status}
                  aria-describedby={errors.status ? 'status-error' : undefined}
                >
                  <option value="">Select status</option>
                  {(isEdit
                    ? [
                        CampaignStatus.DRAFT,
                        CampaignStatus.ACTIVE,
                        CampaignStatus.PAUSED,
                        CampaignStatus.COMPLETED,
                        CampaignStatus.CANCELLED,
                      ]
                    : [CampaignStatus.DRAFT, CampaignStatus.ACTIVE]
                  ).map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
                {errors.status && (
                  <p
                    id="status-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                  >
                    {errors.status}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Container>
  );
};

export default AdvertiseForm;
